/**
 * Joining one board screen to everyone else on that board.
 *
 * Four things are kept in step here: the ink, the hands, the tools people are
 * holding, and what the server says each person is allowed to do. Nothing in this
 * file is ever in the way of drawing — a stroke is on screen before any of it runs
 * (docs/03-ARCHITECTURE.md §7.2).
 *
 * The canvas and the connection are built by two different parts of the screen and
 * arrive in either order, so every join here is written to happen on whichever comes
 * second. Assuming an order is what silently left the pen talking to nobody.
 *
 * Kept free of React so that order can be exercised in a test.
 */

import type { PresenterView } from '@opal/board-doc';
import { canDraw, type Layer, type Point, type Role, type Viewport } from '@opal/shared';

import {
  msUntilNextNotice,
  noticeFor,
  type ConnectionNotice,
} from './connection.js';
import type { EraseSession } from './erase-session.js';
import type { ObjectBoard } from './object-board.js';
import { visibleTo } from './board-layers.js';
import { bindObjectsToBoard } from './object-sync.js';
import {
  followPresenterPage,
  pageActions,
  watchPages,
  writePaper,
  type PageActions,
  type PageHost,
} from './board-pages.js';
import type { SelectSession } from './select-session.js';
import type { ShapeSession } from './shape-session.js';
import type { TextSession } from './text-session.js';
import { bindEditsToBoard, type EditSource } from './board-edit.js';
import type { InkSession } from './ink-session.js';
import { createPresence, type BoardIdentity, type Presence } from './presence.js';
import type { RemoteCursors } from './remote-cursors.js';
import { useBoardStore } from './store.js';
import { bindInkToBoard, type BoardSync, type ConnectionStatus } from './sync.js';

/** The part of the canvas a shared board needs. `BoardHandle` provides it. */
export interface BoardPieces {
  readonly ink: InkSession;
  /** SRS C4 — what the eraser takes off this board goes to everybody else's too. */
  readonly erase: EraseSession;
  /** SRS C4 — so does whatever the select tool moves, resizes or recolours. */
  readonly select: SelectSession;
  /** SRS C4 — a shape drawn here appears on everybody else's board too. */
  readonly shape: ShapeSession;
  /** SRS C4 — and so do the words typed into a text box or a sticky note. */
  readonly text: TextSession;
  /** SRS C5 — a shape that snapped here snaps on everybody else's board too. */
  readonly smartPen: EditSource;
  /** SRS C4 — the laser pointer. Nothing it draws is ever shared or saved. */
  readonly laser: { cancel(): void };
  /** The shapes on this page, kept in step with everyone else's. */
  readonly objects: ObjectBoard;
  readonly cursors: RemoteCursors;
  readonly repaintCursors: () => void;
}

/** Animation frames, so a test never needs a browser to run this. */
export interface Frames {
  request(run: () => void): number;
  cancel(handle: number): void;
}

export interface SharedBoardDeps {
  readonly connect: (identity: BoardIdentity, boardId: string) => BoardSync;
  readonly frames: Frames;
}

export interface SharedBoard {
  /** The canvas layers exist. May happen before or after `open`. */
  attachBoard(board: BoardPieces): void;
  /** Opens the connection to everybody else. May happen before or after `attachBoard`. */
  open(identity: BoardIdentity, boardId: string): void;
  /** This person's hand moved, in canvas coordinates. Null when it leaves the board. */
  pointerMoved(point: Point | null): void;
  /** Called whenever the set of connected people changes, by id. */
  onOnline(handler: (userIds: readonly string[]) => void): void;
  /** Called when what to say about the connection changes (SRS D5). */
  onNotice(handler: (notice: ConnectionNotice) => void): void;
  /** Tries the connection again now. The button on the red banner. */
  retry(): void;
  /** SRS C6 - takes back the last thing this person did, and only theirs. */
  undo(): void;
  redo(): void;
  /** SRS C1 - the pages of this board. */
  readonly pages: PageActions;
  /** SRS D6 - the teacher turning "everybody follow my view" on and off. */
  setPresenting(presenting: boolean): void;
  /** SRS D6 - a student who scrolled away going back to the teacher's view. */
  returnToPresenter(): void;
  /**
   * Called when the "Return to teacher's view" button should appear or go away.
   */
  onCanReturn(handler: (canReturn: boolean) => void): void;
  close(): void;
}

interface State extends PageHost {
  readonly deps: SharedBoardDeps;
  /** SRS C8 - who this is, which decides what they are allowed to be shown. */
  role: Role;
  board: BoardPieces | null;
  unbindInk: (() => void) | null;
  stopListening: (() => void)[];
  frame: number | null;
  online: (userIds: readonly string[]) => void;
  /** When the connection went down, or null while it is up. */
  offlineSince: number | null;
  /** Wakes up exactly when the wording would change, rather than polling a clock. */
  noticeTimer: ReturnType<typeof setTimeout> | null;
  notice: ConnectionNotice;
  onNotice: (notice: ConnectionNotice) => void;
  /** SRS D6 - this device is the one everybody else is following. */
  presenting: boolean;
  /** This device is currently keeping step with the presenter. */
  following: boolean;
  /** Where the presenter is looking, as last announced. */
  presenterView: PresenterView | null;
  /** The exact view this file set, so a view it set is not read as the person moving. */
  appliedView: Viewport | null;
  /** The last view seen at all, so only real changes are considered. */
  lastViewport: Viewport | null;
  canReturn: boolean;
  onCanReturn: (canReturn: boolean) => void;
}

/**
 * Keeps asking for frames while a cursor is still moving or fading.
 *
 * Positions arrive a few times a second and are walked towards, so the frames between
 * them are what turn a series of jumps into a hand crossing the board (SRS D2).
 */
function keepCursorsMoving(state: State): void {
  if (state.frame !== null) return;
  const tick = (): void => {
    state.frame = null;
    const board = state.board;
    if (!board) return;
    const live = board.cursors.step(Date.now());
    board.repaintCursors();
    if (live) state.frame = state.deps.frames.request(tick);
  };
  state.frame = state.deps.frames.request(tick);
}

/** Everyone else on the board, by id, in a stable order. */
function onlineIds(presence: Presence): readonly string[] {
  return [...new Set(presence.others().map((other) => other.userId))].sort();
}

function showOthers(state: State): void {
  const presence = state.presence;
  if (!presence) return;
  state.online(onlineIds(presence));
  if (!state.board) return;
  state.board.cursors.update(presence.others());
  keepCursorsMoving(state);
}

/**
 * Works out what to say about the connection, says it if it has changed, and sets an
 * alarm for the moment it would change again (SRS D5).
 */
function refreshNotice(state: State, status: ConnectionStatus): void {
  if (state.noticeTimer !== null) clearTimeout(state.noticeTimer);
  state.noticeTimer = null;

  const downFor = state.offlineSince === null ? 0 : Date.now() - state.offlineSince;
  const notice = noticeFor(status, downFor);
  if (notice !== state.notice) {
    state.notice = notice;
    state.onNotice(notice);
  }

  const next = msUntilNextNotice(status, downFor);
  if (next === null) return;
  state.noticeTimer = setTimeout(() => {
    state.noticeTimer = null;
    refreshNotice(state, status);
  }, next);
}

/** Follows the connection coming and going, and starts the clock when it goes. */
function watchConnection(state: State, sync: BoardSync): void {
  const seen = (status: ConnectionStatus): void => {
    if (status === 'connected') state.offlineSince = null;
    else state.offlineSince ??= Date.now();
    refreshNotice(state, status);
  };
  state.stopListening.push(sync.onStatus(seen));
  seen(sync.status());
}

/** The view of whichever teacher is presenting, or null if none is. */
function presenterViewOf(state: State): PresenterView | null {
  for (const other of state.presence?.others() ?? []) {
    if (other.role === 'student') continue;
    if (other.view) return other.view;
  }
  return null;
}

function sameView(a: PresenterView | null, b: PresenterView | null): boolean {
  if (a === null || b === null) return a === b;
  return a.zoom === b.zoom && a.panX === b.panX && a.panY === b.panY;
}

function setCanReturn(state: State, canReturn: boolean): void {
  if (state.canReturn === canReturn) return;
  state.canReturn = canReturn;
  state.onCanReturn(canReturn);
}

/**
 * Puts this screen where the presenter is looking.
 *
 * Only the zoom and the position are copied. The size of the window is this device's
 * own, so an iPad following a laptop sees the same part of the board rather than a
 * slice of it with the edges cut off.
 */
function applyPresenterView(state: State, view: PresenterView): void {
  const store = useBoardStore.getState();
  const next: Viewport = {
    ...store.viewport,
    zoom: view.zoom,
    panX: view.panX,
    panY: view.panY,
  };
  state.appliedView = next;
  state.lastViewport = next;
  store.setViewport(next);
}

/**
 * Keeps this screen with the presenter (SRS D6).
 *
 * A teacher switching it on takes everybody with them straight away. A student who
 * then moves the board themselves stops being carried along and is offered a way
 * back, rather than being dragged about while they are trying to look at something.
 */
function followPresenter(state: State): void {
  if (state.presenting) return;
  const view = presenterViewOf(state);
  const hadPresenter = state.presenterView !== null;

  if (view === null) {
    state.presenterView = null;
    state.following = false;
    setCanReturn(state, false);
    return;
  }

  const changed = !sameView(view, state.presenterView);
  state.presenterView = view;
  if (!hadPresenter) {
    state.following = true;
    setCanReturn(state, false);
    applyPresenterView(state, view);
    return;
  }
  if (state.following && changed) applyPresenterView(state, view);
  else if (!state.following) setCanReturn(state, true);
  followPresenterPage(state);
}

/** The part of a viewport that is about what is being looked at. */
function viewOf(viewport: Viewport): PresenterView {
  return { zoom: viewport.zoom, panX: viewport.panX, panY: viewport.panY };
}

/**
 * Notices the person moving the board themselves.
 *
 * A view this file set carries the very object it set, so the two are told apart by
 * identity rather than by comparing numbers that might happen to match.
 *
 * A window changing size is not somebody moving the board. A student turning their
 * iPad, or a banner appearing above the canvas, must not shake off the teacher's view.
 */
function viewportMoved(state: State, viewport: Viewport): void {
  if (viewport === state.lastViewport) return;
  const previous = state.lastViewport;
  state.lastViewport = viewport;
  const moved = !previous || !sameView(viewOf(previous), viewOf(viewport));

  if (state.presenting) {
    if (moved) state.presence?.setView(viewOf(viewport));
    return;
  }
  if (!moved || viewport === state.appliedView || !state.following) return;
  state.following = false;
  setCanReturn(state, state.presenterView !== null);
}

/** Joins the pen and the eraser to the shared board. Waits until both halves exist. */
function bindInk(state: State): void {
  if (state.unbindInk || !state.board || !state.sync) return;
  // SRS C8 — what this person is allowed to be shown. A student's board never takes
  // the teacher-only layer in, so it is not there to be seen by any means at all.
  const visible = (work: { readonly layer: Layer }): boolean =>
    visibleTo(state.role, work);
  const stops = [
    bindInkToBoard(state.board.ink, state.sync, visible),
    bindEditsToBoard(state.board.erase, state.sync),
    bindEditsToBoard(state.board.select, state.sync),
    bindEditsToBoard(state.board.shape, state.sync),
    bindEditsToBoard(state.board.text, state.sync),
    // SRS C5 — a snap reaches everyone else as one change: the ink goes and the
    // clean shape arrives together, never as a deletion with a shape after it.
    bindEditsToBoard(state.board.smartPen, state.sync),
    bindObjectsToBoard(state.board.objects, state.sync, visible),
  ];
  state.unbindInk = () => {
    for (const stop of stops) stop();
  };
}

/** Follows the server's word on what this person may do (SRS D4). */
function watchAccess(state: State, sync: BoardSync): void {
  state.stopListening.push(
    sync.onAccess((access) => {
      useBoardStore.getState().setAccess(access);
      // A line already under way when drawing is taken away is dropped rather than
      // left on this screen alone — the server would refuse it anyway.
      if (!canDraw(access)) {
        state.board?.ink.cancel();
        state.board?.erase.cancel();
        state.board?.select.clear();
        state.board?.shape.cancel();
        state.board?.text.cancel();
      }
      // Out of the class, or the class is over: stop trying to get back in, and let
      // the screen say which it was. Reconnecting would only be refused.
      if (access === 'removed' || access === 'ended') sync.disconnect();
    }),
  );
}

function open(state: State, identity: BoardIdentity, boardId: string): void {
  // A student may not draw until the teacher says so, and the server is what says it.
  // Assuming the stricter answer until it arrives is what stops a first stroke that
  // was never allowed.
  useBoardStore.getState().setAccess(identity.role === 'student' ? 'view' : 'edit');
  state.role = identity.role;

  const sync = state.deps.connect(identity, boardId);
  const presence = createPresence(sync.awareness, identity);
  state.sync = sync;
  state.presence = presence;

  watchAccess(state, sync);
  watchConnection(state, sync);
  watchPages(state, sync);
  state.stopListening.push(
    presence.onChange(() => {
      showOthers(state);
      followPresenter(state);
    }),
  );

  // SRS D2 — the cursor shows the tool that person is holding, so a student can see
  // what the teacher is about to do.
  presence.setTool(useBoardStore.getState().tool);
  state.lastViewport = useBoardStore.getState().viewport;
  state.stopListening.push(
    useBoardStore.subscribe((store) => {
      presence.setTool(store.tool);
      viewportMoved(state, store.viewport);
      // SRS C7 — the paper belongs to the page, so a change to it goes to the page.
      writePaper(state, store.background);
    }),
  );

  bindInk(state);
  showOthers(state);
}

function close(state: State): void {
  if (state.frame !== null) state.deps.frames.cancel(state.frame);
  state.frame = null;
  if (state.noticeTimer !== null) clearTimeout(state.noticeTimer);
  state.noticeTimer = null;
  state.offlineSince = null;
  state.notice = 'none';
  state.onNotice('none');
  state.presenting = false;
  state.following = false;
  state.presenterView = null;
  state.appliedView = null;
  state.lastViewport = null;
  state.canReturn = false;
  state.onCanReturn(false);
  for (const stop of state.stopListening) stop();
  state.stopListening = [];
  state.unbindInk?.();
  state.unbindInk = null;
  state.presence?.destroy();
  state.presence = null;
  state.book = null;
  state.paper = null;
  useBoardStore.getState().setPages([]);
  useBoardStore.getState().setActivePage(null);
  state.sync?.destroy();
  state.sync = null;
  state.board = null;
  // The next board opened in this tab starts from a clean slate, not from whatever
  // the last class had decided about this person.
  useBoardStore.getState().setAccess('edit');
}

/** A board screen with nothing on it and nobody on the other end of it yet. */
function emptyState(deps: SharedBoardDeps): State {
  return {
    deps,
    // The stricter answer until somebody signs in: nothing private is shown by default.
    role: 'student',
    sync: null,
    presence: null,
    book: null,
    paper: null,
    board: null,
    unbindInk: null,
    stopListening: [],
    frame: null,
    online: () => undefined,
    offlineSince: null,
    noticeTimer: null,
    notice: 'none',
    onNotice: () => undefined,
    presenting: false,
    following: false,
    presenterView: null,
    appliedView: null,
    lastViewport: null,
    canReturn: false,
    onCanReturn: () => undefined,
  };
}

/** SRS D6 - the presenting toggle, and a student's way back to the teacher's view. */
function viewActions(state: State): Pick<
  SharedBoard,
  'setPresenting' | 'returnToPresenter' | 'onCanReturn'
> {
  return {
    setPresenting: (presenting) => {
      state.presenting = presenting;
      const viewport = useBoardStore.getState().viewport;
      state.presence?.setView(presenting ? viewOf(viewport) : null);
      if (!presenting) return;
      state.following = false;
      setCanReturn(state, false);
    },
    returnToPresenter: () => {
      const view = state.presenterView;
      if (!view) return;
      state.following = true;
      setCanReturn(state, false);
      applyPresenterView(state, view);
    },
    onCanReturn: (handler) => {
      state.onCanReturn = handler;
    },
  };
}

/** SRS C6 - undo and redo, each touching only this person's own work. */
function workActions(state: State): Pick<SharedBoard, 'undo' | 'redo'> {
  return {
    undo: () => {
      // A student who may not draw may not take work off the board either: the
      // server would refuse it, and their screen would then disagree with everyone's.
      if (canDraw(useBoardStore.getState().access)) state.sync?.undo();
    },
    redo: () => {
      if (canDraw(useBoardStore.getState().access)) state.sync?.redo();
    },
  };
}

/** Builds the wiring for one board screen. Nothing happens until it is given halves. */
export function createSharedBoard(deps: SharedBoardDeps): SharedBoard {
  const state = emptyState(deps);

  return {
    attachBoard(board) {
      state.unbindInk?.();
      state.unbindInk = null;
      state.board = board;
      bindInk(state);
      showOthers(state);
    },
    open: (identity, boardId) => {
      open(state, identity, boardId);
    },
    pointerMoved: (point) => {
      state.presence?.moveTo(point);
    },
    onOnline: (handler) => {
      state.online = handler;
    },
    onNotice: (handler) => {
      state.onNotice = handler;
    },
    retry: () => {
      state.sync?.reconnect();
    },
    ...workActions(state),
    ...viewActions(state),
    pages: pageActions(state),
    close: () => {
      close(state);
    },
  };
}
